import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Save, Eye, Loader2, History, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TemplateEditor = ({ templateKey, onClose }) => {
  const [template, setTemplate] = useState(null);
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState([]);

  const fetchTemplate = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates/${templateKey}`);
      const tpl = res.data.template;
      setTemplate(tpl);
      setSubject(tpl.subject || '');
      setHtmlBody(tpl.html_body || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load template');
    }
  }, [templateKey]);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  // Live preview — refetch when subject/body changes (debounced)
  useEffect(() => {
    if (!template) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/admin/email-templates/${templateKey}/preview`, {
          context: {},
          subject,
          html_body: htmlBody,
        });
        setPreviewSubject(res.data.subject || '');
        setPreviewHtml(res.data.html || '');
        setPreviewError('');
      } catch (err) {
        setPreviewError(err?.response?.data?.detail || 'Preview could not be refreshed. Showing the local draft instead.');
      }
    }, 400);
    return () => clearTimeout(t);
  }, [template, templateKey, subject, htmlBody]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/email-templates/${templateKey}`, {
        subject,
        html_body: htmlBody,
      });
      toast.success(`Saved — now at v${res.data.template.version}`);
      setTemplate(res.data.template);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates/${templateKey}/versions`);
      setVersions(res.data.versions || []);
      setShowHistory(true);
    } catch (err) {
      toast.error('Failed to load history');
    }
  };

  const handleRestore = async (version) => {
    if (!window.confirm(`Restore version ${version}? Current draft will be saved to history.`)) return;
    try {
      await axios.post(`${API}/admin/email-templates/${templateKey}/restore/${version}`);
      toast.success(`Restored from v${version}`);
      setShowHistory(false);
      await fetchTemplate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
  };

  if (!template) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
      </div>
    );
  }

  // Live preview substitutes [var] placeholders for variables so admin sees a realistic render
  const localPreview = (() => {
    const sample = {};
    (template.variables || []).forEach((v) => { sample[v] = `[${v}]`; });
    const sub = (s) => (s || '').replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_, k) => sample[k] ?? '');
    return { subject: sub(subject), html: sub(htmlBody) };
  })();

  return (
    <div className="space-y-4" data-testid="template-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="editor-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-lg font-semibold">{template.name}</h2>
            <p className="text-xs text-gray-500 font-mono">{template.key} · v{template.version}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchHistory} data-testid="editor-history-btn">
            <History className="h-4 w-4 mr-1" /> History
          </Button>
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            onClick={handleSave}
            disabled={saving}
            data-testid="editor-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            {saving ? 'Saving…' : 'Save new version'}
          </Button>
        </div>
      </div>

      {template.variables?.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-xs flex flex-wrap items-center gap-2">
            <span className="font-semibold text-amber-900">Available variables:</span>
            {template.variables.map((v) => (
              <code key={v} className="bg-white border border-amber-300 px-2 py-0.5 rounded text-amber-800">
                {`{{${v}}}`}
              </code>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="editor-subject-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">HTML body</label>
              <textarea
                className="w-full font-mono text-xs border rounded-md p-3 min-h-[480px] focus:outline-none focus:ring-2 focus:ring-green-500"
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                data-testid="editor-html-textarea"
              />
            </div>
          </CardContent>
        </Card>

        {/* Live preview */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                <Eye className="h-3.5 w-3.5" /> Live Preview
              </label>
              <span className="text-[10px] text-gray-400">Placeholders shown as [var]</span>
            </div>
            <div className="border rounded-md p-2 bg-gray-50">
              <p className="text-xs font-medium text-gray-700 mb-2" data-testid="editor-preview-subject">{previewSubject || localPreview.subject}</p>
              {previewError && <p className="text-xs text-amber-700 mb-2" role="alert" data-testid="editor-preview-error">{previewError}</p>}
              <iframe
                title="email-preview"
                srcDoc={previewHtml || localPreview.html}
                className="w-full bg-white rounded"
                style={{ minHeight: 480, border: 'none' }}
                data-testid="editor-preview-iframe"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {showHistory && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Version History</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>Close</Button>
            </div>
            {versions.length === 0 ? (
              <p className="text-sm text-gray-500">No previous versions.</p>
            ) : (
              <div className="space-y-2">
                {versions.map((v) => (
                  <div
                    key={v._id || `${v.template_key}-${v.version}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                    data-testid={`version-row-${v.version}`}
                  >
                    <div>
                      <p className="text-sm font-medium">Version {v.version}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(v.snapshot_at).toLocaleString()} · by {v.snapshot_by}
                      </p>
                      <p className="text-xs text-gray-700 mt-1 italic line-clamp-1">{v.subject}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(v.version)}
                      data-testid={`restore-version-${v.version}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TemplateEditor;
